import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import dayjs from 'lib/dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { queryBlockList, QueryBlockListParams } from 'services/api/blockList';
import { useGlobalQuery } from 'hooks/business/useGlobalQuery';
import ComingPlaceholder from 'views/Aide/components/CommingPlaceholder';
import FilterHeader, { OtherCondition } from './components/FilterHeader';
import BlockListTable, { Container as TableContainer } from './components/Table';

const Container = styled.div`
  padding: 16px;
  height: calc(100vh - var(--header-height) - var(--tabs-height));
  overflow: hidden;
  flex-direction: column;
  display: flex;
`;
const BlockList = React.memo(() => {
  const { t } = useTranslate();
  const globalQuery = useGlobalQuery();
  const [platform, setPlatform] = useState<General.PlatformUpperCase | undefined>(
    globalQuery.platform,
  );
  const [region, setRegion] = useState<General.UpperCaseRegion[]>(
    globalQuery.region ? [globalQuery.region] : [],
  );
  const [other, setOther] = useState<OtherCondition>({
    groupInput: {
      type: 'buyerNick',
      value: '',
    },
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>();
  const [order, setOrder] = useState<'descend' | 'ascend'>('descend');
  const [pagination, setPagination] = useState<{
    pageNo: number;
    pageSize: number;
  }>({
    pageNo: 1,
    pageSize: 10,
  });
  const {
    data: listInfo,
    loading,
    run,
  } = useRequest(
    async (params: QueryBlockListParams) => {
      try {
        const { data, total } = await queryBlockList(params);
        return {
          listData: data,
          total,
        };
      } catch (error) {
        return {
          listData: [],
          total: 0,
        };
      }
    },
    {
      manual: true,
      refreshDeps: [],
    },
  );
  const fetchList = useCallback(() => {
    const { reason, store, groupInput } = other;
    const storeIds = store ? [store] : undefined;
    const reasons = reason ? [reason] : undefined;
    const buyerNick =
      groupInput.type === 'buyerNick' && groupInput.value ? groupInput.value.trim() : undefined;
    const operationNick =
      groupInput.type === 'operatorBy' && groupInput.value ? groupInput.value.trim() : undefined;
    const remark =
      groupInput.type === 'notice' && groupInput.value ? groupInput.value.trim() : undefined;
    const operationStartTime = dateRange?.[0] ? dayjs(dateRange[0]).valueOf() : undefined;
    const operationEndTime = dateRange?.[1]
      ? dayjs(dateRange[1]).endOf('days').valueOf()
      : undefined;
    if (!platform || platform === 'ALIEXPRESS') {
      return;
    }
    run({
      platform,
      storeIds,
      reasons,
      buyerNick,
      operationNick,
      buyerRegions: region.length ? region : undefined,
      operationEndTime,
      operationStartTime,
      remark,
      page: pagination,
      sort: {
        field: 'OPERATION_TIME',
        order: order === 'descend' ? 'desc' : 'asc',
      },
    });
  }, [other, order, dateRange, platform, run, region, pagination]);
  const refresh = useCallback(() => {
    if (listInfo?.listData?.length === 1) {
      setPagination((pre) => {
        const pageNo = pre.pageNo - 1 > 0 ? pre.pageNo - 1 : 1;
        return {
          pageNo,
          pageSize: pre.pageSize,
        };
      });
    } else {
      setPagination((pre) => {
        return {
          ...pre,
        };
      });
    }
  }, [listInfo?.listData]);

  useEffect(() => {
    setPagination((pre) => ({ ...pre, pageNo: 1 }));
  }, [platform, region, dateRange, order]);
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  return (
    <Container>
      <FilterHeader
        platform={platform}
        region={region}
        other={other}
        onPlatformChange={setPlatform}
        onRegionChange={setRegion}
        onOtherChange={setOther}
        onSearch={() => {
          setPagination((pre) => ({ ...pre, pageNo: 1 }));
        }}
      />
      {platform === 'ALIEXPRESS' ? (
        <TableContainer>
          <header>
            <h2>{t('ViewBlockList.table.label')}</h2>
          </header>
          <main>
            <ComingPlaceholder />
          </main>
        </TableContainer>
      ) : (
        <BlockListTable
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          order={order}
          onOrderChange={setOrder}
          pagination={pagination}
          onPageChange={setPagination}
          total={listInfo?.total ?? 0}
          listData={listInfo?.listData ?? []}
          loading={loading}
          refresh={refresh}
        />
      )}
    </Container>
  );
});

export default BlockList;
